import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useAddAutomationStepMutation } from '../../../../queries/Automation/mutations/steps/useAddAutomationStepMutation';
import { useAutomationTemplateRemoveStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateRemoveStepMutation';

export interface UseStepActionsParams {
	automationTemplateId: string;
	steps: Api.IAutomationStep[];
	automationTemplateTrigger?: Api.IAutomationTrigger;
	impersonationContext?: Api.IImpersonationContext;
	createEmptyStep?: () => void;
}

export interface UseStepActionsResult {
	copyStep: (step: Api.IAutomationStep, beforeStepId?: string) => Promise<void>;
	deleteStep: (step: Api.IAutomationStep, clearUnsavedStep: () => void) => Promise<void>;
	moveStepUp: (step: Api.IAutomationStep) => void;
	moveStepDown: (step: Api.IAutomationStep) => void;
	isBusy: boolean;
}

/**
 * Custom hook for handling automation step actions (copy, delete, move up/down).
 *
 * This hook provides operations to manipulate automation template steps, including:
 * - Copying/duplicating steps with schedule adjustment
 * - Deleting steps from the template
 * - Moving steps up or down in the sequence
 *
 * @param params - Configuration object containing template ID, steps array, trigger, and callbacks
 * @returns Object containing action functions and loading state
 */
export function useStepActions(params: UseStepActionsParams): UseStepActionsResult {
	const { automationTemplateId, steps, automationTemplateTrigger, impersonationContext, createEmptyStep } = params;

	const addStepMutation = useAddAutomationStepMutation();
	const removeStepMutation = useAutomationTemplateRemoveStepMutation();

	const isBusy = addStepMutation.isPending || removeStepMutation.isPending;

	/**
	 * Copies a step by creating a new step with the same data in the automation template.
	 * The clone is inserted immediately after the original.
	 * Adjusts the schedule criteria if the new position differs from the original.
	 *
	 * @param step - The step to copy
	 * @param _beforeStepId - Optional ID of the step before which to insert the copy (reserved for future use)
	 */
	const copyStep = useCallback(
		async (step: Api.IAutomationStep, _beforeStepId?: string) => {
			if (!step?.id) {
				if (createEmptyStep) {
					createEmptyStep();
				}
				return;
			}

			const stepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(step);
			if (!stepType) {
				return;
			}

			// Find the step after the original so we can insert before it
			const stepIndex = steps.findIndex(s => s.id === step.id);
			const beforeStepId = stepIndex >= 0 && stepIndex + 1 < steps.length ? steps[stepIndex + 1]?.id : undefined;

			// Build the clone — strip the id so the backend creates a new one
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { id: _id, ...stepWithoutId } = step;
			const clonedStep: Api.IAutomationStep = { ...stepWithoutId };

			// Adjust schedule criteria if needed (e.g. prevent "Immediately" at non-zero index)
			const defaultSchedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(
				stepType,
				automationTemplateTrigger
			);
			if (defaultSchedule.criteria !== step.schedule?.criteria) {
				clonedStep.schedule = {
					...(step.schedule || {}),
					criteria: defaultSchedule.criteria,
				};
			}

			await addStepMutation.mutateAsync({
				templateId: automationTemplateId,
				stepType,
				step: clonedStep,
				beforeStepId,
				impersonationContext,
			});
		},
		[automationTemplateId, automationTemplateTrigger, addStepMutation, impersonationContext, steps, createEmptyStep]
	);

	/**
	 * Deletes a step from the automation template.
	 *
	 * @param step - The step to delete
	 */
	const deleteStep = useCallback(
		async (step: Api.IAutomationStep, clearUnsavedStep: () => void) => {
			const stepIndex = steps.indexOf(step);
			if (stepIndex === -1) {
				return;
			}

			if (!step?.id) {
				clearUnsavedStep();
				return;
			}

			// Determine step type from the automation step
			const stepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(step);
			if (!stepType) {
				return;
			}

			// Call the remove API
			await removeStepMutation.mutateAsync({
				templateId: automationTemplateId,
				stepType,
				stepId: step.id,
				impersonationContext,
			});
		},
		[automationTemplateId, impersonationContext, removeStepMutation, steps]
	);

	/**
	 * Moves a step up in the sequence (decreases index).
	 * This is a local-only operation that updates the array order.
	 *
	 * @param step - The step to move up
	 */
	const moveStepUp = useCallback(
		(step: Api.IAutomationStep) => {
			const stepIndex = steps.indexOf(step);
			if (stepIndex <= 0) {
				return;
			}

			// Swap with the previous step
			const nextSteps = [...steps];
			const temp = nextSteps[stepIndex - 1];
			nextSteps[stepIndex - 1] = nextSteps[stepIndex];
			nextSteps[stepIndex] = temp;

			// Note: Order is maintained locally. The backend persists order based on the
			// sequence of step IDs when the template is saved or published.
		},
		[steps]
	);

	/**
	 * Moves a step down in the sequence (increases index).
	 * This is a local-only operation that updates the array order.
	 *
	 * @param step - The step to move down
	 */
	const moveStepDown = useCallback(
		(step: Api.IAutomationStep) => {
			const stepIndex = steps.indexOf(step);
			if (stepIndex === -1 || stepIndex >= steps.length - 1) {
				return;
			}

			// Swap with the next step
			const nextSteps = [...steps];
			const temp = nextSteps[stepIndex + 1];
			nextSteps[stepIndex + 1] = nextSteps[stepIndex];
			nextSteps[stepIndex] = temp;

			// Note: Order is maintained locally. The backend persists order based on the
			// sequence of step IDs when the template is saved or published.
		},
		[steps]
	);

	return {
		copyStep,
		deleteStep,
		moveStepUp,
		moveStepDown,
		isBusy,
	};
}
